import { useState, useEffect, useCallback } from "react";
import { useGetIdentity, useNotification } from "@refinedev/core";
import { User, ApiKeyOption } from "../../interfaces/user";

interface IMDFFile {
  id: string;
  originalName: string;
  description?: string;
  version?: string;
  category?: string;
  fileSize: number;
  status: 'pending' | 'validating' | 'valid' | 'invalid';
  createdAt: string;
  updatedAt: string;
}

interface UserIMDFResponse {
  success: boolean;
  data: {
    apikey: string;
    imdfFiles: IMDFFile[];
  };
}

const ViewListPage = () => {
  const { data: user } = useGetIdentity<User>();
  const { open: openNotification } = useNotification();
  const [imdfFiles, setImdfFiles] = useState<IMDFFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userApiKey, setUserApiKey] = useState<string | null>(null);
  const [availableApiKeys, setAvailableApiKeys] = useState<ApiKeyOption[]>([]);
  const [selectedApiKey, setSelectedApiKey] = useState<string>("");
  const [apiKeysLoading, setApiKeysLoading] = useState(false);

  // 取得可用的 API Keys (僅限 admin/maintainer)
  const fetchAvailableApiKeys = useCallback(async () => {
    if (!user || !['admin', 'maintainer'].includes(user.role)) return;

    try {
      setApiKeysLoading(true);
      const token = localStorage.getItem("token");
      const API_URL = import.meta.env.VITE_API_URL ?? "http://imdf.sto-tek.com/";
      
      const response = await fetch(`${API_URL}/projects`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('無法取得專案列表');
      }

      const data = await response.json();
      const projects = data.data || [];
      
      // 將專案轉換為 API Key 選項
      const apiKeyOptions: ApiKeyOption[] = projects.map((project: {id: string, name: string, apiKey: string}) => ({
        apiKey: project.apiKey,
        projectName: project.name,
        projectId: project.id,
      }));
      
      setAvailableApiKeys(apiKeyOptions);
      
      // 如果用戶有預設的 apikey，設為選中狀態
      if (user.apikey) {
        setSelectedApiKey(user.apikey);
      } else if (apiKeyOptions.length > 0) {
        // 如果沒有預設 apikey，選擇第一個
        setSelectedApiKey(apiKeyOptions[0].apiKey);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '載入專案失敗';
      openNotification?.({
        type: "error",
        message: "載入錯誤",
        description: errorMessage,
      });
    } finally {
      setApiKeysLoading(false);
    }
  }, [user, openNotification]);

  const fetchIMDFFiles = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      const API_URL = import.meta.env.VITE_API_URL ?? "http://imdf.sto-tek.com/";

      if (['admin', 'maintainer'].includes(user.role)) {
        // Admin/Maintainer: 使用選中的 API Key
        if (!selectedApiKey) {
          setImdfFiles([]);
          setUserApiKey(null);
          return;
        }

        const response = await fetch(`${API_URL}/projects/getIMDFList?apikey=${selectedApiKey}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || '無法取得 IMDF 檔案列表');
        }

        const data = await response.json();
        setImdfFiles(data.data || []);
        setUserApiKey(selectedApiKey);
      } else {
        // 一般用戶: 使用原有的 user-imdf 端點
        const response = await fetch(`${API_URL}/projects/user-imdf`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || '無法取得 IMDF 檔案列表');
        }

        const data: UserIMDFResponse = await response.json();
        setImdfFiles(data.data.imdfFiles);
        setUserApiKey(data.data.apikey);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '載入失敗';
      setError(errorMessage);
      openNotification?.({
        type: "error",
        message: "載入錯誤",
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [user, selectedApiKey, openNotification]);

  // 初始化 API Keys (僅限 admin/maintainer)
  useEffect(() => {
    if (user && ['admin', 'maintainer'].includes(user.role)) {
      fetchAvailableApiKeys();
    }
  }, [user, fetchAvailableApiKeys]);

  // 當 selectedApiKey 改變時重新取得檔案
  useEffect(() => {
    if (user) {
      if (['admin', 'maintainer'].includes(user.role)) {
        if (selectedApiKey) {
          fetchIMDFFiles();
        }
      } else {
        fetchIMDFFiles();
      }
    }
  }, [user, selectedApiKey, fetchIMDFFiles]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      validating: 'bg-blue-100 text-blue-800',
      valid: 'bg-green-100 text-green-800',
      invalid: 'bg-red-100 text-red-800',
    };
    return statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status: string) => {
    const statusText = {
      pending: '待處理',
      validating: '驗證中',
      valid: '有效',
      invalid: '無效',
    };
    return statusText[status as keyof typeof statusText] || status;
  };

  const handleViewFile = (fileId: string) => {
    // 導航到檔案詳情頁面
    window.location.href = `/view/${fileId}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">載入錯誤</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">IMDF 檔案瀏覽</h1>
            <p className="mt-1 text-sm text-gray-500">
              瀏覽您可存取的 IMDF 檔案
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Admin/Maintainer API Key 選擇器 */}
            {user && ['admin', 'maintainer'].includes(user.role) && (
              <div className="flex flex-col space-y-2">
                <label htmlFor="apikey-select" className="text-xs font-medium text-gray-700">
                  選擇專案 API Key
                </label>
                <select
                  id="apikey-select"
                  value={selectedApiKey}
                  onChange={(e) => setSelectedApiKey(e.target.value)}
                  disabled={apiKeysLoading || availableApiKeys.length === 0}
                  className="min-w-[200px] px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {apiKeysLoading ? (
                    <option>載入中...</option>
                  ) : availableApiKeys.length === 0 ? (
                    <option>沒有可用的 API Key</option>
                  ) : (
                    availableApiKeys.map((option) => (
                      <option key={option.apiKey} value={option.apiKey}>
                        {option.projectName} ({option.apiKey.substring(0, 8)}...)
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}
            
            {/* 當前 API Key 顯示 */}
            {userApiKey && (
              <div className="bg-blue-50 px-3 py-2 rounded-md">
                <span className="text-xs text-blue-600 font-mono">
                  當前 API Key: {userApiKey.substring(0, 8)}...
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 檔案列表 */}
      {imdfFiles.length === 0 ? (
        <div className="bg-white shadow-sm rounded-lg p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">沒有可瀏覽的檔案</h3>
          <p className="mt-1 text-sm text-gray-500">
            您的帳戶目前沒有對應專案的 IMDF 檔案，或者尚未設定 API Key。
          </p>
          <p className="mt-2 text-xs text-gray-400">
            如需協助，請聯絡管理員。
          </p>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              檔案列表 ({imdfFiles.length} 個檔案)
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    檔案名稱
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    狀態
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    大小
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    版本
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    分類
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    上傳時間
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {imdfFiles.map((file) => (
                  <tr key={file.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {file.originalName}
                          </div>
                          {file.description && (
                            <div className="text-sm text-gray-500">
                              {file.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(file.status)}`}>
                        {getStatusText(file.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatFileSize(file.fileSize)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {file.version || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {file.category || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(file.createdAt).toLocaleString('zh-TW')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewFile(file.id)}
                        className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md transition-colors"
                      >
                        查看
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewListPage;